import { ButtonGroup, PrimaryButton } from "@digipost/dds-buttons-react";
import { Heading } from "@digipost/dds-heading-react";
import { Input, PasswordInput } from "@digipost/dds-input-react";
import { Modal, ModalActions } from "@digipost/dds-modal-react";
import { isAscii, Strength } from "@digipost/dds-password-security";
import { FC, useState } from "react";
import { useSelector } from "react-redux";

import styles from "./GenerateCertificate.module.css";
import InfoWarningText from "./InfoWarningText";
import {
  findURIWithWindowHostname,
  linkExist,
} from "./infrastructure/linkUtils";
import { ReduxState } from "./infrastructure/redux/state";
import Translated from "./infrastructure/Translated";
import {
  TranslateTextKeyType,
  useTranslate,
} from "./infrastructure/useTranslate";

export const GenerateCertificate: FC = () => {
  const translate = useTranslate();

  const csrfToken = useSelector((state: ReduxState) => state.app.csrfToken);
  const certificates = useSelector((state: ReduxState) =>
    state.org.certificate ? state.org.certificate.certificates : null,
  );
  const generateCertificateLink = useSelector((state: ReduxState) =>
    linkExist("generate_rest_certificate", state.link)
      ? findURIWithWindowHostname("generate_rest_certificate", state.link)
      : null,
  );

  const [password, setPassword] = useState<string>("");
  const [passwordRepeated, setPasswordRepeated] = useState<string>("");

  const [passwordStrength, setPasswordStrength] = useState<
    Strength | undefined
  >();

  const [certificateDownloaded, setCertificateDownloaded] =
    useState<boolean>(false);

  const [dirty, setDirty] = useState<boolean>(false);
  const [certForProcessing, setCertForProcessing] = useState<boolean>(false);

  const formValid = (): {
    hasError: boolean;
    msg: TranslateTextKeyType | null;
  } => {
    const v1 = password;
    const v2 = passwordRepeated;
    const strength = passwordStrength;

    if (v1 && strength?.common)
      return { hasError: true, msg: "password.error_common" };
    if (v1 && !strength?.ok) return { hasError: true, msg: null };
    if (!v1) return { hasError: true, msg: "password.error_blank" };
    if (!v2) return { hasError: true, msg: "password.error_repeat" };
    if (v1 !== v2) return { hasError: true, msg: "password.error_match" };
    if (!isAscii(v1)) return { hasError: true, msg: "password.error_chars" };

    return { hasError: false, msg: null };
  };

  const submitForm = (e: React.ChangeEvent<HTMLFormElement>): void => {
    if (!formValid().hasError) {
      setCertForProcessing(true);
    } else {
      e.preventDefault();
    }
  };

  const hideDialog = (): void => {
    setCertificateDownloaded(true);
  };

  if (!!certificates?.length || certificateDownloaded) {
    return (
      <>
        <p>
          <Translated textKey="generate_certificate.done.p1" />
        </p>
        <p>
          <Translated textKey="generate_certificate.done.p2" allowHtml={true} />
        </p>
      </>
    );
  }

  const validation = formValid();

  if (!generateCertificateLink || !csrfToken) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <form
        action={generateCertificateLink}
        method="post"
        onSubmit={submitForm}
      >
        <Heading level={2} size="medium">
          <Translated textKey="generate_certificate.header" />
        </Heading>
        <p>
          <Translated textKey="generate_certificate.description" />
        </p>
        <p>
          <Translated textKey="generate_certificate.password_requirement" />
        </p>
        <div>
          <PasswordInput
            labelText={translate("generic.password")}
            name="password"
            autoComplete="off"
            valid={passwordStrength?.ok}
            onChange={(val, strength) => {
              setPassword(val);
              setPasswordStrength(strength);
              setDirty(true);
            }}
            strengthText={({ strength }) => translate(`password.s${strength}`)}
          />
          <Input
            type="password"
            labelText={translate("generic.repeat_password")}
            name="repeatedPassword"
            autoComplete="off"
            onChange={(event) => {
              setPasswordRepeated(event.target.value);
              setDirty(true);
            }}
          />
          <input type="hidden" name="token" value={csrfToken} />

          {dirty && validation.hasError && !!validation.msg && (
            <InfoWarningText simple={true} textkey={validation.msg} />
          )}

          <ButtonGroup>
            <PrimaryButton type="submit" disabled={validation.hasError}>
              <Translated textKey="generic.download" />
            </PrimaryButton>
          </ButtonGroup>
        </div>
      </form>

      <Modal
        isOpen={certForProcessing}
        onClose={hideDialog}
        heading={translate("register.org.certificate.header")}
      >
        <Translated textKey="register.org.certificate.text" allowHtml={true} />

        <ModalActions>
          <PrimaryButton onClick={hideDialog}>
            <Translated textKey="register.org.certificate.download_complete" />
          </PrimaryButton>
        </ModalActions>
      </Modal>
    </div>
  );
};
